/**
 * Der gesamte Datenbestand als Live-Abbild von Firestore.
 *
 * Das Tool laedt bewusst alles auf einmal: bei zweistelligen Rechnungszahlen
 * pro Monat sind das wenige tausend Dokumente. Dafuer laufen Prognose und
 * Szenarien vollstaendig im Browser — ein Szenario-Regler reagiert damit
 * sofort, statt auf einen Serveraufruf zu warten.
 */

import {
  agingBuckets, applyScenario, buildForecast, buildDemoDataset, computeVatPeriods,
  monthlyBreakdown, openCents, summarize, todayInBerlin, addMonths, monthKey,
  DEFAULT_SETTINGS,
  type AppSettings, type BankTransaction, type CounterpartyProfile, type Match,
  type MatchRule, type PlannedItem, type Scenario,
} from "@starts/core";
import { collection, doc, onSnapshot, type Unsubscribe } from "firebase/firestore";
import { defineStore } from "pinia";
import { computed, ref, shallowRef } from "vue";
import { dbc } from "@/firebase";

export interface BankAccountDoc {
  id: string;
  displayName: string;
  iban: string | null;
  bankName: string;
  balanceCents: number;
  balanceDate: string | null;
  needsReconnect: boolean;
}

export interface SyncRunDoc {
  startedAt: string;
  finishedAt: string;
  bankTransactions: number;
  autoMatched: number;
  needsReview: number;
  consentWarnings: string[];
  errors: string[];
}

/** Wie weit die Kurve nach vorn schaut. */
const HORIZON_MONTHS = 12;

export const useDataStore = defineStore("data", () => {
  const loading = ref(true);
  const plannedItems = shallowRef<PlannedItem[]>([]);
  const bankTransactions = shallowRef<BankTransaction[]>([]);
  const matches = shallowRef<Match[]>([]);
  const accounts = shallowRef<BankAccountDoc[]>([]);
  const profiles = shallowRef<CounterpartyProfile[]>([]);
  const scenarios = shallowRef<Scenario[]>([]);
  const matchRules = shallowRef<MatchRule[]>([]);
  const syncRuns = shallowRef<SyncRunDoc[]>([]);
  const settings = ref<AppSettings>(DEFAULT_SETTINGS);

  /** Welche Szenarien in der Kurve liegen. Die Baseline ist immer dabei. */
  const activeScenarioIds = ref<string[]>([]);
  const today = ref(todayInBerlin());

  let subscriptions: Unsubscribe[] = [];

  /**
   * Demo-Modus: laedt den Beispieldatensatz statt Firestore.
   *
   * Damit laesst sich die Oberflaeche vollstaendig durchklicken, bevor
   * Bankzugang und MOCO-Schluessel eingerichtet sind — und beim Bauen an der
   * UI muss man nicht jedes Mal echte Konten anfassen.
   */
  function loadDemo(): void {
    const d = buildDemoDataset();
    plannedItems.value = d.plannedItems;
    bankTransactions.value = d.transactions;
    matches.value = d.matches;
    accounts.value = d.accounts as unknown as BankAccountDoc[];
    profiles.value = d.profiles;
    scenarios.value = d.scenarios;
    settings.value = d.settings;
    syncRuns.value = [d.syncRun as unknown as SyncRunDoc];
    loading.value = false;
  }

  function subscribe(): void {
    if (import.meta.env.VITE_DEMO === "true") {
      loadDemo();
      return;
    }
    unsubscribe();
    let pending = 8;
    const settled = (): void => { if (--pending <= 0) loading.value = false; };

    const list = <T>(name: string, target: { value: T[] }): Unsubscribe =>
      onSnapshot(collection(dbc, name), (snap) => {
        target.value = snap.docs.map((d) => ({ ...(d.data() as T), id: d.id }));
        settled();
      }, () => settled());

    subscriptions = [
      list<PlannedItem>("plannedItems", plannedItems),
      list<BankTransaction>("bankTransactions", bankTransactions),
      list<Match>("matches", matches),
      list<BankAccountDoc>("bankAccounts", accounts),
      list<CounterpartyProfile>("counterpartyProfiles", profiles),
      list<Scenario>("scenarios", scenarios),
      list<MatchRule>("matchRules", matchRules),
      onSnapshot(doc(dbc, "settings", "app"), (snap) => {
        // Gemergt, damit ein neu hinzugekommenes Feld nie undefined ist.
        if (snap.exists()) settings.value = { ...DEFAULT_SETTINGS, ...(snap.data() as Partial<AppSettings>) };
        settled();
      }, () => settled()),
      onSnapshot(collection(dbc, "syncRuns"), (snap) => {
        syncRuns.value = snap.docs
          .map((d) => d.data() as SyncRunDoc)
          .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
          .slice(0, 20);
      }),
    ];
  }

  function unsubscribe(): void {
    subscriptions.forEach((u) => u());
    subscriptions = [];
  }

  // ---- Abgeleitetes ---------------------------------------------------------

  const openingBalanceCents = computed(() =>
    accounts.value.reduce((sum, a) => sum + (a.balanceCents ?? 0), 0),
  );

  const horizonEnd = computed(() => `${addMonths(monthKey(today.value), HORIZON_MONTHS)}-01`);

  const baseline = computed(() => scenarios.value.find((s) => s.isBaseline) ?? null);

  /** Kurve des Basisszenarios — Grundlage fuer alle Kennzahlen. */
  const forecast = computed(() =>
    buildForecast({
      today: today.value,
      openingBalanceCents: openingBalanceCents.value,
      plannedItems: applyScenario(plannedItems.value, baseline.value),
      bankTransactions: bankTransactions.value,
      profiles: profiles.value,
      settings: settings.value,
      from: `${addMonths(monthKey(today.value), -3)}-01`,
      to: horizonEnd.value,
    }),
  );

  /** Zusaetzliche Kurven fuer die aktiv geschalteten Szenarien. */
  const scenarioForecasts = computed(() =>
    scenarios.value
      .filter((s) => !s.isBaseline && activeScenarioIds.value.includes(s.id))
      .map((s) => ({
        scenario: s,
        points: buildForecast({
          today: today.value,
          openingBalanceCents: openingBalanceCents.value,
          plannedItems: applyScenario(plannedItems.value, s),
          bankTransactions: bankTransactions.value,
          profiles: profiles.value,
          settings: settings.value,
          from: `${addMonths(monthKey(today.value), -3)}-01`,
          to: horizonEnd.value,
        }),
      })),
  );

  const summary = computed(() =>
    summarize(forecast.value, today.value, settings.value.minimumBalanceCents),
  );

  const monthly = computed(() =>
    monthlyBreakdown(plannedItems.value, today.value, profiles.value, settings.value.applyPaymentBehaviour),
  );

  /**
   * Forderungen im eigentlichen Sinn: was Kunden uns schulden.
   *
   * Nicht jeder erwartete Eingang ist eine Forderung — eine
   * Umsatzsteuer-Erstattung ist Geld vom Finanzamt und gehoert weder in das
   * Debitorenalter noch in das gebundene Kapital. Ohne diese Trennung sieht
   * die Kennzahl groesser aus, als sie ist.
   */
  const customerReceivables = computed(() =>
    plannedItems.value.filter(
      (i) =>
        i.direction === "in" &&
        (i.source === "invoice" || i.source === "paymentSchedule" ||
          (i.source === "manual" && i.category === "revenue")),
    ),
  );

  const receivables = computed(() => agingBuckets(customerReceivables.value, today.value, "in"));

  const openReceivables = computed(() =>
    customerReceivables.value
      .filter((i) => openCents(i) > 0)
      .sort((a, b) => a.originalDate.localeCompare(b.originalDate)),
  );

  const itemsById = computed(() => new Map(plannedItems.value.map((i) => [i.id, i])));
  const transactionsById = computed(() => new Map(bankTransactions.value.map((t) => [t.id, t])));

  /**
   * Alles, was eine Entscheidung braucht — nach Sicherheit sortiert.
   *
   * Vorschlaege zu Posten, an denen nichts mehr offen ist, fallen heraus. Sie
   * entstehen, wenn zwischen zwei Syncs eine Zahlung in MOCO erfasst wird oder
   * eine andere Buchung den Posten deckt: der Vorschlag war beim Erzeugen
   * gueltig und ist es jetzt nicht mehr. Stehenlassen hiesse, eine
   * Entscheidung zu verlangen, die nichts mehr bewirkt — im UI sichtbar als
   * "666,40 € von 0,00 €".
   */
  const inbox = computed(() => {
    const items = itemsById.value;
    return matches.value
      .filter((m) => {
        if (m.status !== "suggested") return false;
        const item = items.get(m.plannedItemId);
        return item ? openCents(item) > 0 : false;
      })
      .sort((a, b) => b.confidence - a.confidence);
  });

  /** Naechste faellige Umsatzsteuer, samt Aufschluesselung fuer das Widget. */
  const nextVatPeriod = computed(() => {
    const periods = computeVatPeriods(plannedItems.value, settings.value.vat);
    return periods.filter((p) => p.dueDate >= today.value).sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0] ?? null;
  });

  const lastSync = computed(() => syncRuns.value[0] ?? null);

  const needsAttention = computed(() => {
    const notes: string[] = [];
    for (const a of accounts.value) {
      if (a.needsReconnect) notes.push(`Bankzugang ${a.bankName} muss neu verbunden werden.`);
    }
    if (lastSync.value?.errors?.length) notes.push(...lastSync.value.errors);
    if (lastSync.value?.consentWarnings?.length) notes.push(...lastSync.value.consentWarnings);
    return notes;
  });

  function toggleScenario(id: string): void {
    const i = activeScenarioIds.value.indexOf(id);
    if (i >= 0) activeScenarioIds.value.splice(i, 1);
    else activeScenarioIds.value.push(id);
  }

  return {
    loading, plannedItems, bankTransactions, matches, accounts, profiles, scenarios, matchRules,
    settings, syncRuns, today, activeScenarioIds,
    subscribe, unsubscribe, toggleScenario, loadDemo,
    openingBalanceCents, forecast, scenarioForecasts, summary, monthly,
    receivables, openReceivables, customerReceivables, inbox, itemsById, transactionsById,
    nextVatPeriod, lastSync, needsAttention, baseline,
  };
});
